import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  HomeIcon,
  ClipboardDocumentListIcon,
  BriefcaseIcon,
  InformationCircleIcon,
  PhoneIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

const navLinks = [
  { label: 'Home', to: '/', icon: HomeIcon },
  { label: 'Records', to: '/records', icon: ClipboardDocumentListIcon },
  { label: 'Disbursements', to: '/projects', icon: BriefcaseIcon },
  { label: 'About', to: '/about', icon: InformationCircleIcon },
  { label: 'Contact', to: '/contact', icon: PhoneIcon },
];

const accountLinks = [
  { label: 'Login as Officer', to: '/login', icon: ArrowRightOnRectangleIcon },
  { label: 'Sign Up as Officer', to: '/signup', icon: UserPlusIcon },
];

// Desktop-only user icon that opens the Login / Sign Up menu.
function AccountMenu({ pathname }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const isAccountPage = accountLinks.some(({ to }) => to === pathname);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`flex items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
          isAccountPage || isOpen ? 'text-gold' : 'text-white hover:text-gold'
        }`}
      >
        <UserIcon className="h-6 w-6" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-3 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {accountLinks.map(({ label, to, icon: Icon }) => (
            <Link
              key={label}
              to={to}
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium hover:bg-gold/10 ${
                pathname === to ? 'text-navy font-bold' : 'text-gray-700'
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandHeader({ onOpenMenu }) {
  const { pathname } = useLocation();

  return (
    <header className="w-full shrink-0 bg-navy border-b border-gray-200 pl-4 pr-2 py-4">
      <div className="flex items-center justify-between gap-4">
        <Link to="/" className="block no-underline">
          <p className="text-sm font-bold text-gold">CALAUAG NATIONAL HIGH SCHOOL</p>
          <h1 className="text-2xl font-bold text-white">SPTA Financial Dashboard</h1>
          <p className="text-sm lg:text-lg italic text-gray-300">
            "Financial Transparency and Accountability"
          </p>
        </Link>

        <nav className="hidden xl:flex flex-wrap items-center gap-6 pr-6">
          {navLinks.map(({ label, to, icon: Icon }) => {
            const isActive = pathname === to;
            return (
              <Link
                key={label}
                to={to}
                className={`flex items-center gap-1.5 text-sm font-medium transition ${
                  isActive ? 'text-gold font-bold' : 'text-white hover:text-gold'
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </Link>
            );
          })}
          <span className="h-5 w-px bg-white/20" aria-hidden="true" />
          <AccountMenu pathname={pathname} />
        </nav>

        {/* Hamburger trigger, mobile only, when a menu handler is provided */}
        {onOpenMenu && (
          <button
            type="button"
            onClick={onOpenMenu}
            className="xl:hidden shrink-0 p-2 text-white"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-8 w-8" />
          </button>
        )}
      </div>
    </header>
  );
}

export default BrandHeader;
